import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { makeRequest } from 'features/entry-form-v2/data/makeRequest';
import { normalizeDate } from 'utils/date';

const GET_PROJECT_APPROVALS = 'getProjectApprovals';
const APPROVE_PROJECT = 'approveProject';
const DECLINE_PROJECT = 'declineProject';

export enum ProjectApprovalStatus {
	PENDING = 'PENDING',
	APPROVED = 'APPROVED',
	REJECTED = 'REJECTED',
	DELETED = 'DELETED'
}

export type ProjectApproval = {
	id: number;
	projectId: number;
	projectName: string;
	requesterUserId: string;
	status: ProjectApprovalStatus;
	createdAt: number[] | string;
	decisionDate: number[] | string | null;
	decisionUserId: string | null;
	organizationId: string;
	organizationName: string;
	archiveNumber: string | null;
	projectType: string | null;
	codeListEnabled: boolean;
};

type ProjectApprovalsResponse = {
	projectApprovals: ProjectApproval[];
	statusCode?: string;
	statusText?: string;
};

type ApproveDeclineInput = {
	projectId: number;
};

class ApiError extends Error {
	statusCode?: string;
	statusText?: string;

	constructor(statusCode: string, statusText: string) {
		super(statusText);
		this.statusCode = statusCode;
		this.statusText = statusText;
	}
}

const projectApprovalsKeys = {
	all: ['projectApprovals'] as const,
	list: () => [...projectApprovalsKeys.all, 'list'] as const
};

export const useProjectApprovals = () => {
	const queryClient = useQueryClient();

	const queryKey = projectApprovalsKeys.list();

	const queryFn = () => {
		return makeRequest<ProjectApprovalsResponse>({
			service: 'projects',
			method: GET_PROJECT_APPROVALS,
			data: {} // Let backend handle organization ID automatically
		});
	};

	const {
		data: response,
		isLoading,
		isFetching,
		isError: isQueryError,
		error: queryError
	} = useQuery(queryKey, queryFn, {
		select: data => {
			if (!data || !data.projectApprovals) {
				return [];
			}
			const approvals = data.projectApprovals;
			// Sort approvals: PENDING first, then by creation date descending
			return [...approvals].sort((a, b) => {
				if (
					a.status === ProjectApprovalStatus.PENDING &&
					b.status !== ProjectApprovalStatus.PENDING
				) {
					return -1;
				}
				if (
					a.status !== ProjectApprovalStatus.PENDING &&
					b.status === ProjectApprovalStatus.PENDING
				) {
					return 1;
				}
				const dateA = new Date(normalizeDate(a.createdAt)).getTime();
				const dateB = new Date(normalizeDate(b.createdAt)).getTime();
				return dateB - dateA; // Descending order
			});
		}
	});

	const invalidateQueries = () => {
		queryClient.invalidateQueries(projectApprovalsKeys.list());
	};

	const approveProjectMutation = useMutation(
		(input: ApproveDeclineInput) =>
			makeRequest({
				service: 'projects',
				method: APPROVE_PROJECT,
				data: { projectId: input.projectId }
			}),
		{
			onSuccess: () => {
				invalidateQueries();
			},
			onError: (error: ApiError) => {
				console.error('Failed to approve project', error);
			}
		}
	);

	const declineProjectMutation = useMutation(
		(input: ApproveDeclineInput) =>
			makeRequest({
				service: 'projects',
				method: DECLINE_PROJECT,
				data: { projectId: input.projectId }
			}),
		{
			onSuccess: () => {
				invalidateQueries();
			},
			onError: (error: ApiError) => {
				console.error('Failed to decline project', error);
			}
		}
	);

	const isMutating = approveProjectMutation.isLoading || declineProjectMutation.isLoading;
	const mutationError = approveProjectMutation.error || declineProjectMutation.error;

	return {
		approvals: response || [],
		isLoading,
		isFetching,
		isQueryError,
		queryError,

		approveProject: approveProjectMutation.mutateAsync,
		declineProject: declineProjectMutation.mutateAsync,

		isMutating,
		mutationError,

		approveProjectMutation,
		declineProjectMutation
	};
};
